import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Mic, Square, Play, Pause, Send, Trash2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface VoiceRecorderProps {
  onSendVoiceMessage: (audioBlob: Blob, transcription?: string) => Promise<boolean>;
  disabled?: boolean;
}

const VoiceRecorder = ({ onSendVoiceMessage, disabled }: VoiceRecorderProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [transcription, setTranscription] = useState<string>("");

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout>();
  const recordedBlobRef = useRef<Blob | null>(null);

  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, []);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      audioChunksRef.current = [];
      setRecordingDuration(0);
      setTranscription("");
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        recordedBlobRef.current = audioBlob;
        setHasRecording(true);
        
        // Create audio URL for playback
        if (audioRef.current) {
          audioRef.current.src = URL.createObjectURL(audioBlob);
        }
        
        // Auto-transcribe
        transcribeAudio(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Start duration timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Recording Error",
        description: "Could not access microphone. Please check permissions.",
        variant: "destructive"
      });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const transcribeAudio = async (audioBlob: Blob) => {
    setIsTranscribing(true);
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Audio = (reader.result as string).split(',')[1];
        
        const { data, error } = await supabase.functions.invoke('voice-to-text', {
          body: { audio: base64Audio }
        });

        if (error) throw error;
        
        if (data?.text) {
          setTranscription(data.text);
          console.log('🎤 Transcription:', data.text);
        }
      };
      reader.readAsDataURL(audioBlob);
    } catch (error) {
      console.error('Transcription error:', error);
      toast({
        title: "Transcription Failed",
        description: "Could not transcribe audio, but you can still send it.",
        variant: "destructive"
      });
    } finally {
      setIsTranscribing(false);
    }
  };

  const playRecording = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
        
        audioRef.current.onended = () => {
          setIsPlaying(false);
        };
      }
    }
  };

  const sendVoiceMessage = async () => {
    if (!recordedBlobRef.current) return;
    
    const success = await onSendVoiceMessage(recordedBlobRef.current, transcription);
    
    if (success) {
      // Reset recorder
      setHasRecording(false);
      setRecordingDuration(0);
      setTranscription("");
      recordedBlobRef.current = null;
      
      if (audioRef.current) {
        audioRef.current.src = "";
      }
    }
  };

  const deleteRecording = () => {
    setHasRecording(false);
    setRecordingDuration(0);
    setTranscription("");
    recordedBlobRef.current = null;
    
    if (audioRef.current) {
      audioRef.current.src = "";
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (disabled) return null;

  return (
    <div className="flex flex-col space-y-3">
      {/* Recording Controls */}
      <div className="flex items-center justify-center space-x-4">
        {!hasRecording ? (
          <Button
            onClick={isRecording ? stopRecording : startRecording}
            className={`h-16 w-16 rounded-full ${
              isRecording 
                ? 'bg-neon-red hover:bg-neon-red/80 animate-pulse' 
                : 'bg-electric-violet hover:bg-electric-violet/80'
            } transition-all duration-200`}
            disabled={disabled}
          >
            {isRecording ? (
              <Square className="w-6 h-6 text-ice-white" />
            ) : (
              <Mic className="w-6 h-6 text-ice-white" />
            )}
          </Button>
        ) : (
          <div className="flex items-center space-x-3">
            {/* Play/Pause */}
            <Button
              onClick={playRecording}
              className="h-12 w-12 rounded-full bg-toxic-green hover:bg-toxic-green/80"
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-carbon" />
              ) : (
                <Play className="w-5 h-5 text-carbon" />
              )}
            </Button>

            {/* Delete */}
            <Button
              onClick={deleteRecording}
              variant="outline"
              className="h-12 w-12 rounded-full border-neon-red text-neon-red hover:bg-neon-red/10"
            >
              <Trash2 className="w-5 h-5" />
            </Button>

            {/* Send */}
            <Button
              onClick={sendVoiceMessage}
              className="h-12 w-12 rounded-full bg-neon-red hover:bg-neon-red/80"
            >
              <Send className="w-5 h-5 text-ice-white" />
            </Button>
          </div>
        )}
      </div>

      {/* Recording Info */}
      {(isRecording || hasRecording) && (
        <div className="text-center space-y-2">
          <div className="text-ice-white text-sm">
            {isRecording ? "Recording..." : "Recording ready"}
            <span className="ml-2 font-mono text-electric-violet">
              {formatDuration(recordingDuration)}
            </span>
          </div>
          
          {isTranscribing && (
            <div className="text-toxic-green text-xs animate-pulse">
              Transcribing...
            </div>
          )}
          
          {transcription && (
            <div className="text-ice-white/80 text-xs bg-carbon/50 rounded-lg p-2 max-w-full">
              "{transcription}"
            </div>
          )}
        </div>
      )}

      {/* Hidden audio element for playback */}
      <audio ref={audioRef} style={{ display: 'none' }} />
    </div>
  );
};

export default VoiceRecorder;